import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import '../styles/NewsPopup.css';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase.js';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import DOMPurify from 'dompurify';

const NewsPopup = ({ news = [] }) => {
  const { currentUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);
  const containerRef = useRef(null);
  const previousActiveRef = useRef(null);

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:v=|v\/|embed\/|youtu.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : null;
  };

  useEffect(() => {
    if (news && news.length > 0) {
      setCurrent(news[0]);
      setVisible(true);
    }
  }, [news]);

  // focus trap + Escape handling when popup is visible
  useEffect(() => {
    if (!visible) return;
    previousActiveRef.current = document.activeElement;
    const node = containerRef.current;
    const focusableSelector = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusable = node ? Array.from(node.querySelectorAll(focusableSelector)).filter(n => !n.hasAttribute('disabled')) : [];
    if (focusable.length) focusable[0].focus();

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setVisible(false);
        return;
      }
      if (e.key === 'Tab') {
        // simple wrap-around
        if (!focusable.length) return;
        const idx = focusable.indexOf(document.activeElement);
        if (e.shiftKey) {
          if (idx === 0) {
            e.preventDefault();
            focusable[focusable.length - 1].focus();
          }
        } else {
          if (idx === focusable.length - 1) {
            e.preventDefault();
            focusable[0].focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      // restore focus
      try { previousActiveRef.current && previousActiveRef.current.focus(); } catch (e) {}
    };
  }, [visible]);

  if (!visible || !current) return null;

  const createdAtText = (() => {
    try {
      if (!current.createdAt) return '';
      if (typeof current.createdAt.toDate === 'function') {
        return current.createdAt.toDate().toLocaleString();
      }
      if (current.createdAt instanceof Date) return current.createdAt.toLocaleString();
      return String(current.createdAt);
    } catch (e) {
      return '';
    }
  })();

  const handleHideForUser = async () => {
    try {
      if (!currentUser) return;
      const profileRef = doc(db, `users/${currentUser.uid}/profile/data`);
      await updateDoc(profileRef, {
        hiddenNews: arrayUnion(current.id)
      });
    } catch (err) {
      console.error('Error al marcar noticia como oculta para el usuario:', err);
    } finally {
      setVisible(false);
    }
  };

  return (
    <div className="news-popup-overlay" role="dialog" aria-modal="true" aria-label="Anuncio">
      <div className="news-popup" tabIndex={-1} ref={containerRef}>
        <button className="news-popup-close" onClick={() => setVisible(false)} aria-label="Cerrar anuncio">
          <X size={16}/>
        </button>
        <div className="news-popup-body">
          <h3 className="news-popup-title">{current.title}</h3>
          {/* If there's a YouTube video, embed a small player; otherwise show link */}
          {current.videoUrl && (() => {
            const yt = extractYouTubeId(current.videoUrl);
            if (yt) {
              return (
                <div className="news-popup-video" style={{ marginBottom: 8 }}>
                  <div className="video-embed" style={{ maxWidth: 360 }}>
                    <iframe
                      title="news-video"
                      width="360"
                      height="203"
                      src={`https://www.youtube.com/embed/${yt}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              );
            }
            return (
              <div className="news-popup-video">
                <a className="news-popup-video-link" href={current.videoUrl} target="_blank" rel="noopener noreferrer">Ver video</a>
              </div>
            );
          })()}

          <div
            className="news-popup-content"
            // content is stored as HTML from the WYSIWYG editor; sanitize before injecting
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(current.content || '') }}
          />

          {createdAtText && <span className="news-popup-date">{createdAtText}</span>}

          <div className="news-popup-actions">
            <button className="lignums-button" onClick={() => setVisible(false)}>Cerrar</button>
            <button className="lignums-button secondary" onClick={handleHideForUser}>No volver a mostrar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsPopup;
